import dayjs from "dayjs";
import { fetchResourcesListData } from "@/utils";

export type RecentAdvisory = {
  id: string;
  advisoryId?: string;
  cveId?: string;
  title: string;
  publishDate?: string;
  slug: string;
};

type AdvisoryListNode = {
  __typename: "ResourcesAdvisory";
  id: string;
  advisoryId?: string | null;
  cveId?: string | null;
  title: string;
  publishDate?: string | null;
  _sys: { filename: string };
};

const TICKER_TITLE_MAX_LENGTH = 52;

export function getAdvisoryHref(slug: string): string {
  return `/advisories/${slug}`;
}

export function formatAdvisoryDisclosureDate(date?: string): string | null {
  if (!date) return null;
  const parsed = dayjs(date);
  return parsed.isValid()
    ? parsed.format("MMM D, YYYY").toUpperCase()
    : null;
}

export function formatAdvisoryTickerLabel(
  advisory: RecentAdvisory,
  { truncateTitle = true }: { truncateTitle?: boolean } = {}
): string {
  const id = advisory.advisoryId || advisory.cveId;
  let title = advisory.title.trim();

  if (truncateTitle && title.length > TICKER_TITLE_MAX_LENGTH) {
    title = `${title.slice(0, TICKER_TITLE_MAX_LENGTH - 1).trimEnd()}…`;
  }

  return id ? `[${id}] ${title}` : title;
}

function toRecentAdvisory(node: AdvisoryListNode): RecentAdvisory {
  const slug = node._sys.filename.replace(/\.mdx$/, "");

  return {
    id: node.id,
    advisoryId: node.advisoryId ?? undefined,
    cveId: node.cveId ?? undefined,
    title: node.title,
    publishDate: node.publishDate ?? undefined,
    slug,
  };
}

export async function fetchRecentAdvisories(
  limit = 8
): Promise<RecentAdvisory[]> {
  const { resourcesData } = await fetchResourcesListData({
    types: ["advisory"],
    limit: 32,
    sort: "publishDate",
  });

  return (resourcesData?.data?.resourcesConnection?.edges ?? [])
    .map((edge) => {
      const node = edge?.node;
      if (!node || node.__typename !== "ResourcesAdvisory") return null;
      return toRecentAdvisory(node as AdvisoryListNode);
    })
    .filter((item): item is RecentAdvisory => item !== null)
    .sort(
      (a, b) =>
        dayjs(b.publishDate).valueOf() - dayjs(a.publishDate).valueOf()
    )
    .slice(0, limit);
}
